import { useEffect, useState } from "react";
import api, { BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, FileText, Download, ShieldCheck, Eye, Sparkles, Loader2 } from "lucide-react";
import { EVIDENCE_TYPE_LABELS } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";

const AI_VERDICT_META = {
  compliant: { label: "Compliant", cls: "bg-green-500/10 text-green-400 border-green-500/30" },
  partially_compliant: { label: "Partial", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  non_compliant: { label: "Non-compliant", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
  not_evidence: { label: "Not evidence", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
};

const MET_META = {
  yes: { icon: "✓", cls: "text-green-400" },
  partial: { icon: "~", cls: "text-yellow-400" },
  no: { icon: "✗", cls: "text-red-400" },
};

function suffMeta(ai) {
  return ai?.sufficient
    ? { label: "PASS · Sufficient", cls: "bg-green-500/15 text-green-300 border-green-500/40" }
    : { label: "FAIL · Insufficient", cls: "bg-red-500/15 text-red-300 border-red-500/40" };
}

export default function Evidence() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin" || user?.role === "assessor";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analysisItem, setAnalysisItem] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/evidence");
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const token = localStorage.getItem("access_token");
  const evidenceUrl = (id) => `${BACKEND_URL}/api/evidence/${id}/download?auth=${token}`;

  const analyze = async (item) => {
    setAnalyzingId(item.id);
    try {
      const { data } = await api.post(`/evidence/${item.id}/analyze`);
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setAnalysisItem(data);
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAnalyzingId(null);
    }
  };

  const filtered = items.filter((e) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      [e.original_filename, e.evidence_type, e.uploaded_by_name].some((x) =>
        x?.toLowerCase().includes(q)
      );
    const matchFilter = filter === "all" || e.strength === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div data-testid="evidence-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Evidence Vault</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Evidence</h1>
          <p className="text-sm text-zinc-500 mt-1">All artifacts uploaded across assessments.</p>
        </div>
        <div className="text-xs text-zinc-500 mono">{filtered.length} / {items.length}</div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search evidence…"
            data-testid="evidence-search"
            className="pl-10 bg-[#121212] border-zinc-800"
          />
        </div>

        <div className="flex gap-1 border border-zinc-800 rounded-md p-1 bg-[#121212]">
          {["all", "verified", "weak", "none"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`evidence-filter-${f}`}
              className={`px-3 py-1.5 rounded text-xs transition-colors ${
                filter === f ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm" data-testid="evidence-empty">
            <FileText className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            No evidence found.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase">File</th>
                <th className="text-left px-4 py-3 label-uppercase">Type</th>
                <th className="text-left px-4 py-3 label-uppercase">Strength</th>
                <th className="text-left px-4 py-3 label-uppercase">Uploaded by</th>
                <th className="text-left px-4 py-3 label-uppercase">Date</th>
                <th className="text-right px-4 py-3 label-uppercase">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 cursor-pointer"
                  data-testid={`evidence-row-${e.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-zinc-500" />
                      <span className="text-white hover:underline">{e.original_filename}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mono mt-0.5">
                      {(e.size / 1024).toFixed(1)} KB
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <Badge className="bg-zinc-900 text-zinc-300 border-zinc-800">
                      {EVIDENCE_TYPE_LABELS[e.evidence_type] || e.evidence_type}
                    </Badge>
                  </td>

                  <td className="px-4 py-3">
                    <Badge
                      className={
                        e.strength === "verified"
                          ? "bg-green-500/10 text-green-400 border-green-500/30"
                          : e.strength === "weak"
                          ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                          : "bg-red-500/10 text-red-400 border-red-500/30"
                      }
                    >
                      {e.strength}
                      {e.verified && <ShieldCheck className="w-3 h-3 ml-1 inline" />}
                    </Badge>
                  </td>

                  <td className="px-4 py-3 text-zinc-300">{e.uploaded_by_name}</td>
                  <td className="px-4 py-3 text-zinc-500 mono text-xs">
                    {e.upload_date?.slice(0, 10)}
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {e.ai_analysis ? (
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); setAnalysisItem(e); }}
                          className="inline-flex items-center gap-1 text-xs"
                          data-testid={`view-analysis-${e.id}`}
                          title="View AI compliance verdict"
                        >
                          <Badge className={suffMeta(e.ai_analysis).cls}>
                            <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />
                            {e.ai_analysis.sufficient ? "PASS" : "FAIL"} · {e.ai_analysis.score}
                          </Badge>
                        </button>
                      ) : canEdit ? (
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); analyze(e); }}
                          disabled={analyzingId === e.id}
                          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-blue-400 disabled:opacity-50"
                          data-testid={`analyze-evidence-${e.id}`}
                          title="Analyze with AI (OCR + compliance grade)"
                        >
                          {analyzingId === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Analyze
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setSelected(e);
                        }}
                        className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
                        data-testid={`view-evidence-${e.id}`}
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>

                      <a
                        href={evidenceUrl(e.id)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(ev) => ev.stopPropagation()}
                        data-testid={`download-evidence-${e.id}`}
                        className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
                      >
                        <Download className="w-3 h-3" /> Download
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EvidencePreviewModal
        evidence={selected}
        url={selected ? evidenceUrl(selected.id) : ""}
        onClose={() => setSelected(null)}
      />

      <AnalysisDialog item={analysisItem} onClose={() => setAnalysisItem(null)} />
    </div>
  );
}

function EvidencePreviewModal({ evidence, url, onClose }) {
  if (!evidence) return null;

  const filename = evidence.original_filename || "";
  const contentType = evidence.content_type || "";
  const lower = filename.toLowerCase();

  const isPdf = contentType.includes("pdf") || lower.endsWith(".pdf");
  const isImage =
    contentType.startsWith("image/") ||
    [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((x) => lower.endsWith(x));
  const isText =
    contentType.startsWith("text/") ||
    [".txt", ".log", ".csv", ".json"].some((x) => lower.endsWith(x));

  return (
    <Dialog open={!!evidence} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-6xl h-[88vh] p-0 overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b border-zinc-800">
          <DialogTitle className="text-base flex items-center justify-between gap-3">
            <span className="truncate">{filename}</span>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white shrink-0"
            >
              <Download className="w-3 h-3" /> Download
            </a>
          </DialogTitle>

          <div className="flex flex-wrap gap-2 pt-2">
            <Badge className="bg-zinc-900 text-zinc-300 border-zinc-800">
              {EVIDENCE_TYPE_LABELS[evidence.evidence_type] || evidence.evidence_type}
            </Badge>
            <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800">
              {(evidence.size / 1024).toFixed(1)} KB
            </Badge>
            <Badge
              className={
                evidence.strength === "verified"
                  ? "bg-green-500/10 text-green-400 border-green-500/30"
                  : evidence.strength === "weak"
                  ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                  : "bg-red-500/10 text-red-400 border-red-500/30"
              }
            >
              {evidence.strength}
            </Badge>
            {evidence.ai_analysis && (
              <Badge className={suffMeta(evidence.ai_analysis).cls}>
                <Sparkles className="w-2.5 h-2.5 mr-0.5 inline" />
                AI: {evidence.ai_analysis.sufficient ? "PASS" : "FAIL"} · {evidence.ai_analysis.score}
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="h-full bg-[#0A0A0A]">
          {isPdf ? (
            <iframe
              src={url}
              title={filename}
              className="w-full h-full border-0"
              data-testid="evidence-pdf-preview"
            />
          ) : isImage ? (
            <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
              <img
                src={url}
                alt={filename}
                className="max-w-full max-h-full rounded border border-zinc-800"
                data-testid="evidence-image-preview"
              />
            </div>
          ) : isText ? (
            <iframe
              src={url}
              title={filename}
              className="w-full h-full border-0 bg-white"
              data-testid="evidence-text-preview"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <FileText className="w-12 h-12 text-zinc-700 mb-4" />
              <div className="text-white font-medium">Preview not available for this file type.</div>
              <p className="text-sm text-zinc-500 mt-2 max-w-md">
                This evidence can still be downloaded and reviewed using the appropriate application.
              </p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-black text-sm hover:bg-zinc-200"
              >
                <Download className="w-4 h-4" /> Download Evidence
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnalysisDialog({ item, onClose }) {
  const ai = item?.ai_analysis;
  const m = ai ? (AI_VERDICT_META[ai.verdict] || AI_VERDICT_META.not_evidence) : null;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg max-h-[85vh] overflow-auto" data-testid="analysis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-blue-400" /> AI Evidence Analysis
          </DialogTitle>
        </DialogHeader>
        {ai && (
          <div className="space-y-3 text-sm">
            <div className="text-xs text-zinc-500 mono truncate">{item.original_filename}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={suffMeta(ai).cls}>{suffMeta(ai).label}</Badge>
              <Badge className={m.cls}>{m.label}</Badge>
              <span className="text-zinc-400">Score <span className="text-white mono">{ai.score}/100</span></span>
              <span className="text-zinc-500">Suggested strength: <span className={ai.suggested_strength === "verified" ? "text-green-400" : ai.suggested_strength === "weak" ? "text-yellow-400" : "text-red-400"}>{ai.suggested_strength}</span></span>
            </div>
            {ai.document_type && <div className="text-xs text-zinc-500">Document type: <span className="text-zinc-300">{ai.document_type}</span></div>}
            {ai.summary && <div className="text-zinc-300">{ai.summary}</div>}
            {ai.rationale && (
              <div className={`text-[13px] ${ai.sufficient ? "text-green-300/90" : "text-red-300/90"}`}>
                <span className="text-zinc-500">Why it {ai.sufficient ? "passed" : "failed"}: </span>{ai.rationale}
              </div>
            )}
            {ai.requirements_check?.length > 0 && (
              <div>
                <div className="label-uppercase mb-1">Requirement checks</div>
                <ul className="space-y-1 text-[13px]">
                  {ai.requirements_check.map((c, i) => {
                    const mm = MET_META[c.met] || MET_META.no;
                    return (
                      <li key={i} className="flex gap-2">
                        <span className={`${mm.cls} mono shrink-0`} title={c.met}>{mm.icon}</span>
                        <span><span className="text-zinc-200">{c.requirement}</span>{c.note ? <span className="text-zinc-500"> — {c.note}</span> : null}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {(ai.reasons_pass || ai.key_findings || []).length > 0 && (
              <div>
                <div className="label-uppercase mb-1">Why it passes</div>
                <ul className="list-disc list-inside text-green-300/80 text-[13px] space-y-0.5">
                  {(ai.reasons_pass || ai.key_findings).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            {(ai.reasons_fail || ai.gaps || []).length > 0 && (
              <div>
                <div className="label-uppercase mb-1">Why it falls short</div>
                <ul className="list-disc list-inside text-yellow-300/80 text-[13px] space-y-0.5">
                  {(ai.reasons_fail || ai.gaps).map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              </div>
            )}
            {ai.extracted_text && (
              <details className="text-zinc-500 text-xs">
                <summary className="cursor-pointer hover:text-zinc-300">Extracted text (OCR)</summary>
                <pre className="mt-1 whitespace-pre-wrap text-[11px] text-zinc-400 max-h-64 overflow-auto bg-[#0A0A0A] p-2 rounded">{ai.extracted_text}</pre>
              </details>
            )}
            <div className="text-[10px] text-zinc-600 pt-2 border-t border-zinc-800">
              Advisory only — the AI verdict does not change the evidence strength or assessment score. Use Verify in the assessment workspace to set strength. {ai.model ? `Model: ${ai.model}` : ""}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
